import { I18n } from 'react-redux-i18n';
import {
  BrandExpandButton,
  BrandWineRows
} from '../../components/BrandWineRows';
import React from 'react';
import styled from 'styled-components';
import { SectionInfo } from '../../components/SectionInfo';
import { SearchInput } from '../../components/SearchInput';
import { ColumnDef } from '../../components/ColumnDisplayModal';
import { Pagination } from '../../components/Pagination';
import { DistributionGlobalDataFilter } from 'apiTyped/hooks/global_data_view.query';
import {
  CardDesc,
  CardHead,
  CardHeadActions,
  CardTitle,
  CardTitleRow,
  ColDisplayBadge,
  ColDisplayTrigger,
  ExportButton,
  GroupBy,
  SortableTh,
  Table,
  TableWrap,
  Td,
  Tr
} from './shared';
import { TableSkeleton } from './loading';
import type { GlobalViewRow, TableSort } from './types';

/**
 * Key Indicators table columns — mirrors proto's `KEY_COLUMNS`.
 * The `/global_data_view/wines` endpoint supplies the KPI fields
 * used here: penetration, visibility, loyalty, consumption and vintage data.
 */
export const KEY_COLUMNS: ColumnDef[] = [
  {
    id: 'name',
    label: I18n.t('v2GlobalView.keyIndicators.columns.name'),
    defaultOn: true,
    always: true
  },
  {
    id: 'color',
    label: I18n.t('v2GlobalView.keyIndicators.columns.color'),
    defaultOn: false
  },
  {
    id: 'nd',
    label: I18n.t('v2GlobalView.keyIndicators.columns.nd'),
    defaultOn: true
  },
  {
    id: 'pr',
    label: I18n.t('v2GlobalView.keyIndicators.columns.pr'),
    defaultOn: true
  },
  {
    id: 'abv',
    label: I18n.t('v2GlobalView.keyIndicators.columns.abv'),
    defaultOn: true
  },
  {
    id: 'posLost',
    label: I18n.t('v2GlobalView.keyIndicators.columns.posLost'),
    defaultOn: true
  },
  {
    id: 'posGained',
    label: I18n.t('v2GlobalView.keyIndicators.columns.posGained'),
    defaultOn: true
  },
  {
    id: 'price',
    label: I18n.t('v2GlobalView.keyIndicators.columns.price'),
    defaultOn: true
  },
  {
    id: 'loyalty',
    label: I18n.t('v2GlobalView.keyIndicators.columns.loyalty'),
    defaultOn: true
  },
  {
    id: 'consumption',
    label: I18n.t('v2GlobalView.keyIndicators.columns.consumption'),
    defaultOn: false
  },
  {
    id: 'vintage',
    label: I18n.t('v2GlobalView.keyIndicators.columns.vintage'),
    defaultOn: false
  },
  {
    id: 'older',
    label: I18n.t('v2GlobalView.keyIndicators.columns.older'),
    defaultOn: false
  }
];

export const KeyIndicatorsTable = styled(Table)`
  table-layout: fixed;

  th {
    white-space: normal;
    overflow-wrap: anywhere;
    line-height: 1.35;
  }

  th:first-child {
    width: 21%;
  }
`;

export const KeyRowChild = styled(Tr)`
  background: var(--paper, #f9f7f4) !important;

  td {
    color: var(--ink-muted, #6b675e);
    font-size: 12.5px;
  }
`;

export const KeyTreeConnector = styled.span`
  display: inline-block;
  color: var(--ink-faint, #8a8578);
  margin: 0 8px 0 22px;
`;

export interface KeyIndicatorsTabProps {
  expandedBrands: Set<string | number>;
  groupBy: GroupBy;
  isLoading: boolean;
  isExporting: boolean;
  isLoadingKey: boolean;
  onExport: () => void;
  keyColVisibility: Record<string, boolean>;
  keyQueryFilter: DistributionGlobalDataFilter;
  keySort: TableSort<GlobalViewRow>;
  keyTotalPages: number;
  page: number;
  paginatedKeyData: GlobalViewRow[];
  renderKeyMetricCells: (row: GlobalViewRow) => React.ReactNode;
  search: string;
  setKeyColModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  toggleExpandedBrand: (key: string | number) => void;
  user: { isBranded: boolean };
}

export const KeyIndicatorsTab: React.FC<KeyIndicatorsTabProps> = ({
  expandedBrands,
  groupBy,
  isLoading,
  isExporting,
  isLoadingKey,
  onExport,
  keyColVisibility,
  keyQueryFilter,
  keySort,
  keyTotalPages,
  page,
  paginatedKeyData,
  renderKeyMetricCells,
  search,
  setKeyColModalOpen,
  setPage,
  setSearch,
  toggleExpandedBrand,
  user
}) => (
  <TableWrap>
    <CardHead>
      <div>
        <CardTitleRow>
          <CardTitle>
            My Products & My Competition
            <SectionInfo
              title="My Products & My Competition"
              body={
                <>
                  <p>
                    Follow distribution, availability, loyalty and price metrics
                    for the selected scope.
                  </p>
                  <p>
                    Branded customers see brand-level performance; other
                    customers see wine-level detail.
                  </p>
                </>
              }
            />
          </CardTitle>
        </CardTitleRow>
        <CardDesc>
          Compare distribution, availability, loyalty, consumption and price for
          the selected markets and filters.
        </CardDesc>
      </div>
      <CardHeadActions>
        <SearchInput
          placeholder="Search wine or brand…"
          value={search}
          onChange={setSearch}
        />
        <ColDisplayTrigger
          type="button"
          onClick={() => setKeyColModalOpen(true)}
        >
          Columns{' '}
          <ColDisplayBadge>
            {Object.values(keyColVisibility).filter(Boolean).length}
          </ColDisplayBadge>
        </ColDisplayTrigger>
        <ExportButton onClick={onExport} disabled={isExporting} />
      </CardHeadActions>
    </CardHead>
    {isLoading || isLoadingKey ? (
      <TableSkeleton
        label={I18n.t('v2GlobalView.keyIndicators.loading')}
        columns={Math.max(
          Object.values(keyColVisibility).filter(Boolean).length,
          4
        )}
      />
    ) : (
      <KeyIndicatorsTable>
        <thead>
          <tr>
            <SortableTh
              label={I18n.t('v2GlobalView.keyIndicators.columns.name')}
              sortKey="name"
              sort={keySort.sort}
              onSort={keySort.onSort}
            />
            {keyColVisibility.color && groupBy === 'wine' && (
              <SortableTh
                label={I18n.t('v2GlobalView.keyIndicators.columns.color')}
                sortKey="color"
                sort={keySort.sort}
                onSort={keySort.onSort}
              />
            )}
            {keyColVisibility.nd && (
              <SortableTh
                label={I18n.t('v2GlobalView.keyIndicators.columns.nd')}
                sortKey="nd"
                sort={keySort.sort}
                onSort={keySort.onSort}
                align="right"
              />
            )}
            {keyColVisibility.pr && (
              <SortableTh
                label={I18n.t('v2GlobalView.keyIndicators.columns.pr')}
                sortKey="pr"
                sort={keySort.sort}
                onSort={keySort.onSort}
                align="right"
              />
            )}
            {keyColVisibility.abv && (
              <SortableTh
                label={I18n.t('v2GlobalView.keyIndicators.columns.abv')}
                sortKey="abv"
                sort={keySort.sort}
                onSort={keySort.onSort}
                align="right"
              />
            )}
            {keyColVisibility.posLost && (
              <SortableTh
                label={I18n.t('v2GlobalView.keyIndicators.columns.posLost')}
                sortKey="posLost"
                sort={keySort.sort}
                onSort={keySort.onSort}
                align="right"
              />
            )}
            {keyColVisibility.posGained && (
              <SortableTh
                label={I18n.t('v2GlobalView.keyIndicators.columns.posGained')}
                sortKey="posGained"
                sort={keySort.sort}
                onSort={keySort.onSort}
                align="right"
              />
            )}
            {keyColVisibility.price && (
              <SortableTh
                label={I18n.t('v2GlobalView.keyIndicators.columns.price')}
                sortKey="price"
                sort={keySort.sort}
                onSort={keySort.onSort}
                align="right"
              />
            )}
            {keyColVisibility.loyalty && (
              <SortableTh
                label={I18n.t('v2GlobalView.keyIndicators.columns.loyalty')}
                sortKey="loyalty"
                sort={keySort.sort}
                onSort={keySort.onSort}
                align="right"
              />
            )}
            {keyColVisibility.consumption && (
              <SortableTh
                label={I18n.t('v2GlobalView.keyIndicators.columns.consumption')}
                sortKey="consumption"
                sort={keySort.sort}
                onSort={keySort.onSort}
                align="right"
              />
            )}
            {keyColVisibility.vintage && (
              <SortableTh
                label={I18n.t('v2GlobalView.keyIndicators.columns.vintage')}
                sortKey="vintage"
                sort={keySort.sort}
                onSort={keySort.onSort}
              />
            )}
            {keyColVisibility.older && (
              <SortableTh
                label={I18n.t('v2GlobalView.keyIndicators.columns.older')}
                sortKey="older"
                sort={keySort.sort}
                onSort={keySort.onSort}
                align="right"
              />
            )}
          </tr>
        </thead>
        <tbody>
          {paginatedKeyData.map((row: any, i: number) => {
            const rowKey = row.entity_id ?? row.entity_name ?? i;
            const expandable =
              user.isBranded &&
              groupBy === 'brand' &&
              typeof row.entity_id === 'number';
            const isOpen = expandable && expandedBrands.has(rowKey);
            return (
              <React.Fragment key={rowKey}>
                <Tr className={isOpen ? 'is-open' : undefined}>
                  <Td>
                    {expandable && (
                      <BrandExpandButton
                        name={row.entity_name}
                        expanded={isOpen}
                        onClick={() => toggleExpandedBrand(rowKey)}
                      />
                    )}
                    <strong>{row.entity_name}</strong>
                  </Td>
                  {keyColVisibility.color && groupBy === 'wine' && (
                    <Td>{row.color || '—'}</Td>
                  )}
                  {renderKeyMetricCells(row)}
                </Tr>
                {isOpen && (
                  <BrandWineRows
                    brandId={row.entity_id}
                    filter={keyQueryFilter}
                    colSpan={
                      1 +
                      Object.entries(keyColVisibility).filter(
                        ([column, visible]) => column !== 'color' && visible
                      ).length
                    }
                    renderRow={(wine, wi) => (
                      <KeyRowChild key={`${rowKey}-${wine.entity_id ?? wi}`}>
                        <Td>
                          <KeyTreeConnector>└</KeyTreeConnector>
                          {wine.entity_name}
                        </Td>
                        {renderKeyMetricCells(wine)}
                      </KeyRowChild>
                    )}
                  />
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </KeyIndicatorsTable>
    )}
    <Pagination
      page={page}
      totalPages={keyTotalPages}
      onPrevious={() => setPage((p) => Math.max(1, p - 1))}
      onNext={() => setPage((p) => Math.min(keyTotalPages, p + 1))}
    />
  </TableWrap>
);
